// ============================================================================
// FSA REPORT SERVICE
// ============================================================================

import { downloadFile, get, put, post } from '../../client/axiosClient';
import { fsaReportEndpoints } from '../../endpoints/fsa/fsaReport.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  EngagementReportData,
  ReportHydrate,
  ReportListItem,
  SaveReportDataRequest,
  ExcludeReportRequest,
} from '../../types/fsa/reportData.types';

class FsaReportService {
  async list(engagementId: ID): Promise<ApiResponse<ReportListItem[]>> {
    const r = await get<ApiResponse<ReportListItem[]>>(fsaReportEndpoints.listByEngagement(engagementId));
    return r.data;
  }

  async hydrate(engagementId: ID, reportId: ID): Promise<ApiResponse<ReportHydrate>> {
    const r = await get<ApiResponse<ReportHydrate>>(fsaReportEndpoints.hydrate(engagementId, reportId));
    return r.data;
  }

  async save(
    engagementId: ID,
    reportId: ID,
    request: SaveReportDataRequest,
  ): Promise<ApiResponse<EngagementReportData>> {
    const r = await put<ApiResponse<EngagementReportData>>(
      fsaReportEndpoints.save(engagementId, reportId), request,
    );
    return r.data;
  }

  /** FSA-2.2 — mark a report as not-applicable. Returns 409 on saved-data conflict. */
  async exclude(
    engagementId: ID,
    reportId: ID,
    request: ExcludeReportRequest,
  ): Promise<ApiResponse<EngagementReportData>> {
    const r = await post<ApiResponse<EngagementReportData>>(
      fsaReportEndpoints.exclude(engagementId, reportId), request,
    );
    return r.data;
  }

  async include(
    engagementId: ID,
    reportId: ID,
  ): Promise<ApiResponse<EngagementReportData>> {
    const r = await post<ApiResponse<EngagementReportData>>(
      fsaReportEndpoints.include(engagementId, reportId), {},
    );
    return r.data;
  }

  /**
   * Server-rendered per-report PDF. Returns a Blob the preview modal
   * renders inside an iframe — same blob-iframe pattern used by the
   * FSA template / adjustment-journal PDFs.
   */
  async getPdfBlob(engagementId: ID, reportId: ID): Promise<Blob> {
    const response = await get<Blob>(
      fsaReportEndpoints.pdf(engagementId, reportId),
      { responseType: 'blob' },
    );
    return response.data;
  }

  /** ADR-0024 R-4 — editable Word of a sectioned report (Director's Report + annexures), downloaded as `fileName`. */
  downloadDocx(engagementId: ID, reportId: ID, fileName: string): Promise<void> {
    return downloadFile(fsaReportEndpoints.docx(engagementId, reportId), fileName);
  }

  /**
   * FSA-2.3 Roll Forward — import the prior engagement's saved narrative
   * report content (placeholders + body) verbatim. Auditor edits year refs
   * + opinion language afterward.
   */
  async importFromPriorYear(
    engagementId: ID,
    reportId: ID,
  ): Promise<ApiResponse<import('../../types/fsa/rollForward.types').ImportFromPyResult>> {
    const r = await post<ApiResponse<import('../../types/fsa/rollForward.types').ImportFromPyResult>>(
      fsaReportEndpoints.importFromPriorYear(engagementId, reportId),
      {},
    );
    return r.data;
  }
}

export const fsaReportService = new FsaReportService();
export default fsaReportService;
